import { AfterContentInit, Component, ContentChildren, ElementRef, inject, Input, QueryList, Renderer2 } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { BehaviorSubject, Subscription, timer } from 'rxjs';
import { filter } from 'rxjs/operators';

@UntilDestroy()
@Component({
  selector: 'kh-fixed-size-carousel',
  templateUrl: './fixed-size-carousel.component.html',
  styleUrls: ['./fixed-size-carousel.component.less'],
  standalone: false
})
export class FixedSizeCarouselComponent implements AfterContentInit {
  private readonly renderer = inject(Renderer2);

  private activeIndex = new BehaviorSubject(0);
  private slideOffsets: number[] = [];

  private autoplaySubscription: Subscription | null = null;
  private isAutoPlayEnabled = true;

  @ContentChildren('carouselItem') public contentChildren!: QueryList<ElementRef>;
  public defaultDotDiameter = 6;
  public defaultActiveColour = '#869791';
  public defaultInactiveColour = '#D6D6D6';

  @Input() public autoLoopTimeoutMs = 0;
  @Input() public height = '338px';
  @Input() public dotDiameter: number | null = null;
  @Input() public dotActiveColour: string | null = null;
  @Input() public dotInactiveColour: string | null = null;

  public itemCount = 0;

  public activeIndex$ = this.activeIndex.asObservable();

  public ngAfterContentInit(): void {
    this.initializeSlides(this.contentChildren);
    this.contentChildren.changes
      .pipe(untilDestroyed(this))
      .subscribe((items: QueryList<ElementRef>) => this.initializeSlides(items));
  }

  public getHeight() {
    return { maxHeight: this.height };
  }

  private initializeSlides(items: QueryList<ElementRef>): void {
    this.slideOffsets = items.map((_) => 0);
    this.activeIndex.next(0);
    this.itemCount = items.length;

    if (items.length > 0) {
      const firstItem = items.first.nativeElement;
      let yOffset = -firstItem.clientHeight;
      this.renderer.addClass(firstItem, 'animated');
      for (let index = 1; index < items.length; index++) {
        const item = items.get(index)!;
        this.renderer.addClass(item.nativeElement, 'animated');
        this.renderer.setStyle(item.nativeElement, 'visibility', 'hidden');
        yOffset -= item.nativeElement.clientHeight;
      }
    }

    this.intitializeAutoloopTimer();
  }

  private intitializeAutoloopTimer(): void {
    if (this.autoplaySubscription) {
      this.autoplaySubscription.unsubscribe();
    }

    if (this.autoLoopTimeoutMs) {
      this.autoplaySubscription = timer(this.autoLoopTimeoutMs, this.autoLoopTimeoutMs)
        .pipe(
          untilDestroyed(this),
          filter(() => this.isAutoPlayEnabled))
        .subscribe(() => this.goToNextSlide());
    }
  }

  public onDotClicked(index: number): void {
    const currentIndex = this.activeIndex.value;

    if (this.activeIndex.value !== index) {
      this.activeIndex.next(index);

      this.setVisible(currentIndex, false);
      this.setVisible(index, true);
    }
  }

  public setAutoplayEnabled(isEnabled: boolean): void {
    this.isAutoPlayEnabled = isEnabled;
  }

  private setVisible(index: number, isVisible: boolean): void {
    const slide = this.contentChildren.get(index)?.nativeElement;

    if (slide) {
      if (isVisible) {
        if (index && !this.slideOffsets[index]) {
          let yOffset = 0;
          for (let i = 0; i < index; i++) {
            yOffset -= this.contentChildren.get(i)?.nativeElement?.clientHeight;
          }
          this.slideOffsets[index] = yOffset;
          this.renderer.setStyle(slide, 'transform', `translateY(${yOffset}px)`);
        }
        this.renderer.setStyle(slide, 'visibility', 'visible');
        this.renderer.removeClass(slide, 'fadeOut');
        this.renderer.addClass(slide, 'fadeIn');
      }
      else {
        this.renderer.removeClass(slide, 'fadeIn');
        this.renderer.addClass(slide, 'fadeOut');
      }
    }
  }

  private goToNextSlide(): void {
    const nextIndex = (this.activeIndex.value + 1) % this.itemCount;
    this.onDotClicked(nextIndex);
  }
}
